import { httpsCallable } from 'firebase/functions';
import type {
  ConfirmCardPaymentInput,
  ConfirmCardPaymentResult,
  CreateBookingInput,
  CreateBookingResult,
  GetAvailabilityResponse,
  GetExperienceOptionsResponse,
  GetExperiencePricingResponse,
  GetExperiencesResponse,
  StartCardPaymentInput,
  StartCardPaymentResult,
} from '@flyingmuse/shared';
import { functions } from './firebase';

/**
 * Public hosted booking-engine base. Used to build a checkout fallback link so
 * "Book" always works even if our callable layer is unavailable.
 */
export const BOOKING_BASE_URL = 'https://flyingmuse.link-twist.com';

/** Hosted-checkout URL for a product alias (fallback / external link). */
export function hostedBookingUrl(alias: string): string {
  return `${BOOKING_BASE_URL}/product/${alias}`;
}

const callGetExperiences = httpsCallable<unknown, GetExperiencesResponse>(
  functions,
  'getExperiences',
);
const callGetExperienceOptions = httpsCallable<
  { productId: number },
  GetExperienceOptionsResponse
>(functions, 'getExperienceOptions');
const callGetAvailability = httpsCallable<
  { productId: number; optionId: number; from: string; to: string },
  GetAvailabilityResponse
>(functions, 'getAvailability');
const callGetExperiencePricing = httpsCallable<
  { productId: number },
  GetExperiencePricingResponse
>(functions, 'getExperiencePricing');
const callCreateBooking = httpsCallable<CreateBookingInput, CreateBookingResult>(
  functions,
  'createBooking',
);
const callStartCardPayment = httpsCallable<
  StartCardPaymentInput,
  StartCardPaymentResult
>(functions, 'startCardPayment');
const callConfirmCardPayment = httpsCallable<
  ConfirmCardPaymentInput,
  ConfirmCardPaymentResult
>(functions, 'confirmCardPayment');

export async function getExperiences(): Promise<GetExperiencesResponse> {
  return (await callGetExperiences()).data;
}

export async function getExperienceOptions(
  productId: number,
): Promise<GetExperienceOptionsResponse> {
  return (await callGetExperienceOptions({ productId })).data;
}

export async function getAvailability(
  productId: number,
  optionId: number,
  from: string,
  to: string,
): Promise<GetAvailabilityResponse> {
  return (await callGetAvailability({ productId, optionId, from, to })).data;
}

export async function getExperiencePricing(
  productId: number,
): Promise<GetExperiencePricingResponse> {
  return (await callGetExperiencePricing({ productId })).data;
}

export async function createBooking(
  input: CreateBookingInput,
): Promise<CreateBookingResult> {
  return (await callCreateBooking(input)).data;
}

/**
 * Hold the slot and open a Viva payment order. The caller redirects the browser
 * to the returned `checkoutUrl`; the booking is only confirmed once the payment
 * clears.
 */
export async function startCardPayment(
  input: StartCardPaymentInput,
): Promise<StartCardPaymentResult> {
  return (await callStartCardPayment(input)).data;
}

/** Confirm the outcome of a Smart Checkout redirect. Safe to poll. */
export async function confirmCardPayment(
  input: ConfirmCardPaymentInput,
): Promise<ConfirmCardPaymentResult> {
  return (await callConfirmCardPayment(input)).data;
}
